import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Modal, Button, Badge, Spinner, Card } from 'react-bootstrap';
import { useAuth } from './AuthContext';
import { subscriptionService } from '../services/admin/subscriptionService';
import { STORAGE_KEYS } from '../utils/constants';
import { useNavigate } from 'react-router-dom';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    return {
      subscription: null,
      loading: true,
      hasAccess: true,
      isExpired: false,
      isInGrace: false,
      daysRemaining: null,
      refreshSubscription: () => {},
    };
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showBlockingModal, setShowBlockingModal] = useState(false);

  const checkIntervalRef = useRef(null);

  // Only check for admin users
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const fetchSubscription = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false);
      setHasAccess(true);
      return;
    }

    try {
      const response = await subscriptionService.getStatus();
      if (response.success) {
        const data = response.data;
        setSubscription(data.subscription);
        setAvailablePlans(data.available_plans || []);

        if (!data.has_subscription || !data.subscription) {
          // No subscription record yet — treat as new/trial account, allow access without blocking
          setHasAccess(true);
          setShowBlockingModal(false);
        } else {
          const sub = data.subscription;
          const expired = sub.is_expired === true;
          const inGrace = sub.is_in_grace === true;
          const isTrial = parseFloat(sub.amount_paid || 0) === 0;

          if (expired) {
            // Fully expired (paid or trial) — block access
            setHasAccess(false);
            setShowBlockingModal(true);
          } else if (inGrace) {
            setHasAccess(true);
            setShowBlockingModal(false);
            maybeShowReminder(sub);
          } else if (isTrial) {
            // Active trial — full access, no popup unless ending in ≤ 3 days
            setHasAccess(true);
            setShowBlockingModal(false);
            if (sub.days_remaining <= 3) {
              maybeShowReminder(sub);
            }
          } else if (sub.days_remaining <= 3 && sub.days_remaining >= 0) {
            setHasAccess(true);
            setShowBlockingModal(false);
            maybeShowReminder(sub);
          } else {
            setHasAccess(true);
            setShowBlockingModal(false);
          }
        }
      }
    } catch (error) {
      console.error('Subscription check error:', error);
      // On error, don't block - let user continue
      setHasAccess(true);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  /**
   * Reminder frequency control: once per day
   * Uses localStorage to track when reminder was last shown
   */
  const maybeShowReminder = (sub) => {
    const today = new Date().toDateString();
    const lastDateStr = localStorage.getItem(STORAGE_KEYS.SUBSCRIPTION_REMINDER_LAST);

    // Already shown today — skip
    if (lastDateStr === today) return;

    setShowReminderModal(true);
    localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION_REMINDER_LAST, today);
  };

  // Fetch on mount and set interval for periodic checks (every 30 min)
  useEffect(() => {
    fetchSubscription();

    if (isAdmin) {
      checkIntervalRef.current = setInterval(fetchSubscription, 30 * 60 * 1000);
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [fetchSubscription, isAdmin]);

  const refreshSubscription = useCallback(async () => {
    setLoading(true);
    await fetchSubscription();
  }, [fetchSubscription]);

  const daysRemaining = subscription?.days_remaining ?? null;
  const isExpired = subscription?.is_expired === true;
  const isInGrace = subscription?.is_in_grace === true;
  const graceDaysRemaining = subscription?.grace_days_remaining ?? 0;

  // Memoize context value to prevent cascading re-renders
  const contextValue = useMemo(
    () => ({
      subscription,
      availablePlans,
      loading,
      hasAccess,
      isExpired,
      isInGrace,
      daysRemaining,
      graceDaysRemaining,
      refreshSubscription,
    }),
    [
      subscription,
      availablePlans,
      loading,
      hasAccess,
      isExpired,
      isInGrace,
      daysRemaining,
      graceDaysRemaining,
      refreshSubscription,
    ]
  );

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}

      {/* Reminder Modal - shown D-3, D-2, D-1, D-0 before expiry, and during grace. Once per day. */}
      <ReminderModal
        show={showReminderModal}
        onHide={() => setShowReminderModal(false)}
        subscription={subscription}
        daysRemaining={daysRemaining}
        isInGrace={isInGrace}
        graceDaysRemaining={graceDaysRemaining}
        onRenew={() => {
          setShowReminderModal(false);
          navigate('/admin/subscription');
        }}
      />

      {/* Blocking Modal - shown when subscription is fully expired */}
      <BlockingModal
        show={showBlockingModal && isAdmin && !loading}
        subscription={subscription}
        onRenew={() => {
          setShowBlockingModal(false);
          navigate('/admin/subscription');
        }}
      />
    </SubscriptionContext.Provider>
  );
};

// =============================================
// REMINDER MODAL (dismissible, D-2/D-1/grace)
// =============================================
const ReminderModal = ({ show, onHide, subscription, daysRemaining, isInGrace, graceDaysRemaining, onRenew }) => {
  if (!show) return null;

  const isTrial = subscription && parseFloat(subscription.amount_paid) === 0;
  const entity = isTrial ? 'free trial' : 'subscription';
  const entityCap = isTrial ? 'Free Trial' : 'Subscription';

  const getTitle = () => {
    if (isInGrace) return `${entityCap} Expired - Grace Period`;
    if (daysRemaining === 0) return `${entityCap} Expires Today!`;
    if (daysRemaining === 1) return `${entityCap} Expires Tomorrow!`;
    return `${entityCap} Expiring Soon`;
  };

  const getMessage = () => {
    if (isInGrace) {
      return `Your ${entity} has expired. You have ${graceDaysRemaining} grace day${graceDaysRemaining !== 1 ? 's' : ''} remaining. After the grace period, your account will be locked. Please ${isTrial ? 'upgrade to a paid plan' : 'renew'} immediately.`;
    }
    if (daysRemaining === 0) {
      return `Your ${entity} expires today. ${isTrial ? 'Upgrade to a paid plan' : 'Renew now'} to avoid interruption to your services.`;
    }
    if (daysRemaining === 1) {
      return `Your ${entity} expires tomorrow. ${isTrial ? 'Upgrade to a paid plan' : 'Renew now'} to ensure uninterrupted access.`;
    }
    return `Your ${entity} expires in ${daysRemaining} days. ${isTrial ? 'Upgrade to a paid plan' : 'Renew now'} to ensure uninterrupted access.`;
  };

  const getBadgeVariant = () => {
    if (isInGrace) return 'danger';
    if (daysRemaining <= 1) return 'warning';
    return 'info';
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="md">
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="w-100 text-center">
          <i className="bi bi-exclamation-triangle-fill text-warning me-2" style={{ fontSize: '1.5rem' }}></i>
          <span>{getTitle()}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center py-4">
        <div className="mb-3">
          <Badge bg={getBadgeVariant()} className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
            {isInGrace
              ? `Grace Period: ${graceDaysRemaining} day${graceDaysRemaining !== 1 ? 's' : ''} left`
              : daysRemaining === 0
                ? 'Expires Today'
                : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`}
          </Badge>
        </div>
        <p className="text-muted mb-3">{getMessage()}</p>
        {subscription && (
          <div className="bg-light rounded p-3 mb-3 d-inline-block">
            <small className="text-muted d-block">{isTrial ? 'Trial Plan' : 'Current Plan'}</small>
            <strong>
              {subscription.plan_name}
              {isTrial && (
                <Badge bg="info" className="ms-2" style={{ fontSize: '0.7rem' }}>
                  Free Trial
                </Badge>
              )}
            </strong>
            <small className="text-muted d-block">
              Expires:{' '}
              {new Date(subscription.end_date).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </small>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 justify-content-center gap-2 pt-0">
        <Button variant="outline-secondary" onClick={onHide}>
          Remind Me Later
        </Button>
        <Button variant="primary" onClick={onRenew}>
          <i className="bi bi-arrow-repeat me-1"></i>
          {isTrial ? 'Upgrade Now' : 'Renew Now'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// =============================================
// BLOCKING MODAL (non-dismissible, expired)
// =============================================
const BlockingModal = ({ show, subscription, onRenew }) => {
  const { logout } = useAuth();

  if (!show) return null;

  return (
    <Modal show={show} centered backdrop="static" keyboard={false} size="md">
      <Modal.Body className="text-center py-5">
        <div className="mb-4">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10"
            style={{ width: '80px', height: '80px' }}
          >
            <i className="bi bi-lock-fill text-danger" style={{ fontSize: '2.5rem' }}></i>
          </div>
        </div>

        <h4 className="mb-3">Subscription Expired</h4>

        <p className="text-muted mb-4">
          Your subscription has expired and your account is currently locked. Please renew your subscription to regain
          access to all features.
        </p>

        {subscription && (
          <Card className="border-0 bg-light mb-4 mx-auto" style={{ maxWidth: '300px' }}>
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">Plan</small>
                <small className="fw-semibold">{subscription.plan_name || 'N/A'}</small>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">Expired On</small>
                <small className="fw-semibold text-danger">
                  {subscription.end_date
                    ? new Date(subscription.end_date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </small>
              </div>
              {subscription.grace_end_date && (
                <div className="d-flex justify-content-between">
                  <small className="text-muted">Grace Ended</small>
                  <small className="fw-semibold text-danger">
                    {new Date(subscription.grace_end_date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <div className="d-flex justify-content-center gap-3">
          <Button variant="outline-secondary" onClick={logout}>
            <i className="bi bi-box-arrow-left me-1"></i>
            Sign Out
          </Button>
          <Button variant="primary" size="lg" onClick={onRenew}>
            <i className="bi bi-arrow-repeat me-1"></i>
            Renew Subscription
          </Button>
        </div>

        <div className="mt-3">
          <small className="text-muted">Need help? Contact support for assistance with your subscription.</small>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SubscriptionContext;
